import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import AuthTextArea from "../Input/AuthTextArea";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { UpdateLoanPaymentApi } from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import PaymentAccountSelector from "../Selector/PaymentAccountSelector";
import { fetchAccounts } from "../../store/Slices/AccountSlice";
import { fetchLoanLog, fetchLoans } from "../../store/Slices/LoanSlice";
import { toLocalYmd } from "../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user")) || {};
const { branchId: defaultBranchId = "" } = userData;

const toDateInput = (unix) => {
  if (!unix) return "";
  return toLocalYmd(unix);
};

const resolveAccountFromRow = (row, accounts) => {
  const acc = row?.accountId;
  if (acc && typeof acc === "object" && acc._id) return acc;
  const id = acc?._id || acc;
  if (id) {
    const found = accounts.find((a) => String(a._id) === String(id));
    if (found) return found;
  }
  if (row?.accountName) {
    const found = accounts.find(
      (a) =>
        String(a.account_name || "").toUpperCase() ===
        String(row.accountName).toUpperCase()
    );
    if (found) return found;
  }
  return "";
};

const EditLoanPaymentModal = ({ open, setOpen, entry, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [curDate, setCurDate] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const AccountState = useSelector((state) => state.AccountState);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchAccounts());
  }, [open, dispatch]);

  useEffect(() => {
    if (!open || !entry) return;
    setAmount(String(entry.amount ?? ""));
    setDesc(entry.desc || "");
    setCurDate(toDateInput(entry.date) || toLocalYmd());
    const accounts = Array.isArray(AccountState.data) ? AccountState.data : [];
    setSelectedAccount(resolveAccountFromRow(entry, accounts));
  }, [open, entry, AccountState.data]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const id = entry?.sourceId || entry?._id;
    if (!id) {
      WarningToast("Invalid repayment entry");
      setLoading(false);
      return;
    }
    const parsed = Number(amount);
    if (!parsed || parsed <= 0) WarningToast("Enter valid amount");
    else if (!selectedAccount?._id) WarningToast("Select account");
    else if (!curDate) WarningToast("Select date");
    else {
      try {
        const response = await UpdateLoanPaymentApi(id, {
          amount: parsed,
          accountId: selectedAccount._id,
          branchId: entry.branchId?._id || entry.branchId || defaultBranchId,
          date: curDate,
          desc,
        });
        if (response.data.success) {
          SuccessToast("Repayment updated");
          setOpen(false);
          dispatch(fetchLoans());
          dispatch(fetchLoanLog());
          dispatch(fetchAccounts());
          onSuccess?.();
        } else {
          ErrorToast(response.data.error?.msg);
        }
      } catch (err) {
        ErrorToast(err.response?.data?.error?.msg || "Failed to update repayment");
      }
    }
    setLoading(false);
  };

  if (!entry) return null;

  return (
    <CustomModal open={open} setOpen={setOpen}>
      <div className="pb-8">
        <h1 className="w-full text-center font-[700] text-2xl py-6">
          Edit repayment
        </h1>
        {entry.partyName && (
          <p className="text-center text-sm text-inkSecondary mb-4">{entry.partyName}</p>
        )}
        <div className="flex flex-col gap-y-4 px-10">
          <PaymentAccountSelector
            label="Receive repayment into"
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
          />
          <AuthInput
            label="Amount"
            Type="number"
            Value={amount}
            setValue={setAmount}
          />
          <AuthInput label="Date" Type="date" Value={curDate} setValue={setCurDate} />
          <AuthTextArea label="Notes" Value={desc} setValue={setDesc} />
        </div>
        <div className="flex justify-center gap-x-2 mt-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn title="Save" onSubmit={onSubmit} type={1} />
              <AuthBtn title="Cancel" onSubmit={() => setOpen(false)} type={2} />
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default EditLoanPaymentModal;
